'use client';

import { useActionState, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useFormStatus } from 'react-dom';
import { Alert, Button, MenuItem, Stack, TextField } from '@mui/material';

import { submitContactAction, type ContactActionState } from '../actions';
import { contactSubmissionSchema } from '@/libs/contact-submission-schema';

const initialContactActionState: ContactActionState = {
  success: false,
  message: '',
};

const fields = [
  { name: 'name', label: 'Seu nome', type: 'text', autoComplete: 'name' },
  { name: 'email', label: 'Seu e-mail', type: 'email', autoComplete: 'email' },
  { name: 'subject', label: 'Assunto', type: 'text', autoComplete: 'off' },
] as const;

export function ContactForm() {
  const [state, action] = useActionState(submitContactAction, initialContactActionState);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (state.success) {
      formRef.current?.reset();
      setClientErrors({});
    }
  }, [state.success]);

  useEffect(() => {
    const firstError = Object.keys(clientErrors)[0] ?? Object.keys(state.fieldErrors ?? {})[0];
    if (firstError)
      (formRef.current?.elements.namedItem(firstError) as HTMLInputElement | HTMLTextAreaElement | null)?.focus();
  }, [clientErrors, state.fieldErrors]);

  const validateBeforeSubmit = (event: FormEvent<HTMLFormElement>) => {
    const data = new FormData(event.currentTarget);
    const result = contactSubmissionSchema.safeParse(Object.fromEntries(data.entries()));
    if (!result.success) {
      event.preventDefault();
      setClientErrors(result.error.flatten().fieldErrors as Record<string, string>);
      return;
    }
    setClientErrors({});
  };

  return (
    <form ref={formRef} action={action} onSubmit={validateBeforeSubmit}>
      <Stack spacing={2}>
        {state.message && (
          <Alert severity={state.success ? 'success' : 'error'} role={state.success ? 'status' : 'alert'}>
            {state.message}
          </Alert>
        )}
        {fields.map((field) => (
          <TextField
            key={field.name}
            name={field.name}
            label={field.label}
            type={field.type}
            autoComplete={field.autoComplete}
            required
            fullWidth
            error={Boolean(clientErrors[field.name] ?? state.fieldErrors?.[field.name])}
            helperText={clientErrors[field.name] ?? state.fieldErrors?.[field.name]}
            FormHelperTextProps={{ id: `${field.name}-error` }}
            aria-describedby={`${field.name}-error`}
          />
        ))}
        <TextField name='type' label='Tipo de contato' select defaultValue='SUPORTE' required fullWidth>
          <MenuItem value='SUPORTE'>Suporte</MenuItem>
          <MenuItem value='BUG'>Reportar um bug</MenuItem>
          <MenuItem value='SUGESTAO'>Sugestão</MenuItem>
          <MenuItem value='OUTRO'>Outro</MenuItem>
        </TextField>
        <TextField
          name='message'
          label='Mensagem'
          multiline
          minRows={5}
          required
          fullWidth
          error={Boolean(clientErrors.message ?? state.fieldErrors?.message)}
          helperText={clientErrors.message ?? state.fieldErrors?.message ?? 'Até 5.000 caracteres.'}
          FormHelperTextProps={{ id: 'message-error' }}
          aria-describedby='message-error'
        />
        <TextField
          name='website'
          label='Website'
          tabIndex={-1}
          autoComplete='off'
          sx={{ display: 'none' }}
          aria-hidden='true'
        />
        <SubmitButton />
      </Stack>
    </form>
  );
}

function SubmitButton() {
  const { pending } = useFormStatus();
  return (
    <Button type='submit' variant='contained' size='large' disabled={pending} sx={{ alignSelf: 'flex-start' }}>
      {pending ? 'Enviando…' : 'Enviar mensagem'}
    </Button>
  );
}
